import { useContext, useEffect, useRef } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { useValue } from '@hooks/useValue'
import { useDelayedInput } from '@hooks/useDelayedInput'

import { useCommercialOffers } from '@api/commercialOffers/state'
import { useLogin } from '@api/login/state'

import { CommercialOffers } from './ui'
import { CommercialOffersFilter } from './ui.filter'
import { ICommercialOffers, IFilterCommercialOffers, IAccessSites } from './types'

import { IModalConfirm, ModalConfirm } from '@primitive/modal.confirm'

import { CardModeType } from '@smart/common.type'
import { CardCommercialOffersContainer, ICardCommercialOffersContainer } from '@smart/cardKP'

import { NotificationContext, notifyOpen } from '@ui/notification/notification.provider'


/** Комерческие предложения - Контейнер */
export const CommercialOffersContainer = () => {
    const notify = useContext(NotificationContext)
   
    /** Метка о том, что доступ к данному разделу запрещен */
    const isAccessDenied = useValue<boolean>(false)

    const filterIsOpen = useValue<boolean>(false)
    const filterIsActivate = useValue<boolean>(false)
    const filterSites = useValue<IAccessSites[]>([])

    const cardCommercialOffer = useValue<ICardCommercialOffersContainer>({isOpen: false})
    const modalConfirm = useValue<IModalConfirm>({isOpen: false})
    
    const paginationPageNumber = useValue<number>(1)

    const {
        commercialOffersError, isReloadCommercialOffers, commercialOffers,
        commercialOffersCreated, commercialOffersDelayedCall, commercialOffersSended,
        commercialOffersExecutionContract, commercialOffersCanceled,
        clearCommercialOffersState, actionGetCommercialOffers,
        actionSendTelegramMessageClientKP,
    } = useCommercialOffers(useShallow((state) => ({
        commercialOffersError: state.error,
        isReloadCommercialOffers: state.isReloadCommercialOffers,
        commercialOffers: state.commercialOffers,

        commercialOffersCreated: state.commercialOffers.created,
        commercialOffersDelayedCall: state.commercialOffers.delayedCall,
        commercialOffersSended: state.commercialOffers.sended,
        commercialOffersExecutionContract: state.commercialOffers.executionContract,
        commercialOffersCanceled: state.commercialOffers.canceled,

        clearCommercialOffersState: state.clearCommercialOffersState,
        actionGetCommercialOffers: state.actionGetCommercialOffers,
        actionSendTelegramMessageClientKP: state.actionSendTelegramMessageClientKP,
    })))

    // Хранилище - Текущий пользователь
    const { loginAccessSites } = useLogin(useShallow((state) => ({
        loginAccessSites: state.profile.accessSites
    })))

    const filtersRef = useRef<string>('')

    // ================================================================================

    /** Функция по загрузке данных основного списка */
    const loadPrimaryData = (search: string) => {
        actionGetCommercialOffers(paginationPageNumber.value, search, filtersRef.current).then((res) => {
            if (res.status !== 200 && res.status !== 500) {
                if (res.status === 403) {
                    clearCommercialOffersState()
                    isAccessDenied.setValue(true)
                } else {
                    notify && notifyOpen(res.message, 'error', 2000, notify)
                }
            }
        })
    }

    /** Запуск загрузки данных через полсекунды */
    const { value: txtSearch, setValue: setTxtSearch } = useDelayedInput<string>("", 500, (val) => loadPrimaryData(val))

    // ================================================================================

    useEffect(() => {
        return (() => {
            clearCommercialOffersState()
            paginationPageNumber.setValue(1)
            setTxtSearch('')

            filterIsOpen.setValue(false)
            filterIsActivate.setValue(false)
            filterSites.setValue([])
        })
    }, []) // eslint-disable-line react-hooks/exhaustive-deps
   
    useEffect(() => {
        loadPrimaryData(txtSearch)
    }, [paginationPageNumber.value]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        isReloadCommercialOffers && loadPrimaryData(txtSearch)
    }, [isReloadCommercialOffers]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (commercialOffersError) {
            notify && notifyOpen(commercialOffersError.message, commercialOffersError.type, 3000, notify)
        }
    }, [commercialOffersError]) // eslint-disable-line react-hooks/exhaustive-deps


    
    // ================================================================================

    useEffect(() => {
        if (loginAccessSites && loginAccessSites.length > 0) {
            filterSites.setValue(
                loginAccessSites.map((item) => ({
                    id: item.siteDbNameID,
                    name: item.siteDbName,
                    description: item.siteDbNameDescription,
                    isSelected: true
                }))
            )
        }
    }, [loginAccessSites]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        let accessSitesListSelected = ''

        filterSites.value.forEach((item) => {
            if (item.isSelected) {
                accessSitesListSelected += `'${item.name}',`
            }
        })

        filtersRef.current = accessSitesListSelected
    }, [filterSites.value])

    /** Кнопка применить (очистить) в окне - Фильтр */
    const handleApplyClearFilter = (filterType: 'apply' | 'clear') => {
        switch (filterType) {
            case 'apply': {
                // Фильтр активен только если хотя бы один сайт выключен
                const isFilterActive = filterSites.value.some(site => !site.isSelected)
                filterIsActivate.setValue(isFilterActive)
                loadPrimaryData(txtSearch)
                break
            }

            case 'clear': {
                filterIsActivate.setValue(false)
                filterSites.setValue(filterSites.value.map(status => ({...status, isSelected: true})))

                actionGetCommercialOffers(paginationPageNumber.value, txtSearch).then(() => {
                    filterIsActivate.setValue(false)
                })
                break
            }
        }
    }

    // ================================================================================

    /** Открытие/закрытие анкеты коммерческого предложения */
    const handleOpenCardMain = (isOpen: boolean, cardMode: CardModeType, cardID?: number) => {
        cardCommercialOffer.setValue({
            isOpen, cardMode, cardID,
            handleButtonCancel() { cardCommercialOffer.setValue({isOpen: false}) }, 
            handleButtonOk() {
                cardCommercialOffer.setValue({isOpen: false})
            }
        })
    }

    /** Открытие/закрытие окна подтверждения */
    const handlerOpenModalConfirm = async (isOpen: boolean, headerText: string, contentText: string, actionName?: string, recordID?: number) => {
        modalConfirm.setValue({
            isOpen, headerText, contentText, 
            handleButtonCancel() { modalConfirm.setValue({isOpen: false}) }, 
            handleButtonOk() {
                switch (actionName) {
                    case 'sendedTelegram': {
                        actionSendTelegramMessageClientKP(Number(recordID)).then((res) => {
                            if (res.status !== 200) notify && notifyOpen(res.message, 'error', 2000, notify)
                        })
                        break
                    } // Отправка информации в telegram
                }

                modalConfirm.setValue({isOpen: false})
            }
        })
    }

    // ================================================================================

    /** Свойства передаваемые в компоненту */
    const propsToComponent: ICommercialOffers = {
        txtSearch, setTxtSearch, isAccessDenied,

        filterIsOpen, filterIsActivate,

        commercialOffersList: commercialOffers.items,
        isLoading: commercialOffers.isLoading,
        paginationPageNumber, 
        totalCount: commercialOffers.totalCount, 
        pageSize: commercialOffers.pageSize,
        commercialOffersCreated, commercialOffersDelayedCall, commercialOffersSended,
        commercialOffersExecutionContract, commercialOffersCanceled,
        
        loadPrimaryData, handleOpenCardMain, handlerOpenModalConfirm,
        handleApplyClearFilter,
    }

    /** Свойства передаваемые в окно фильтра */
    const propsToFilter: IFilterCommercialOffers = {
        isOpen: filterIsOpen.value,

        filterSites,

        handleButtonCancel: () => filterIsOpen.setValue(false),
        handleApplyClearFilter,
    }
    
    return <>
        <CommercialOffers {...propsToComponent} />
        <CommercialOffersFilter {...propsToFilter} />
        <CardCommercialOffersContainer {...cardCommercialOffer.value} />
        <ModalConfirm {...modalConfirm.value} />
    </>
}